"use client";

import Link from "next/link";
import Gift from "lucide-react/dist/esm/icons/gift";
import ArrowRight from "lucide-react/dist/esm/icons/arrow-right";
import Trophy from "lucide-react/dist/esm/icons/trophy";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { useReferrals } from "@/hooks/useReferrals";
import { REFERRAL_GOAL, REFERRAL_REWARD_DAYS, referralProgress, referralsRemaining } from "@/lib/referrals";

/**
 * Promotes the refer-a-friend reward: invite {REFERRAL_GOAL} friends who verify
 * their email → a free week of Buyer Plus (unlimited downloads + unlimited free
 * claims + 5% off + more). Routes to the full /dashboard/referrals surface.
 *
 * The reward grants the `buyer_plus` tier — for a buyer that's effectively
 * "unlimited downloads for a week", which is the headline we lead with for the
 * free-download cohort.
 */
export function ReferralProCard({ compact = false, className }: { compact?: boolean; className?: string }) {
  const { data } = useReferrals();
  const verified = data?.verifiedCount ?? 0;
  const earned = !!data?.rewardGrantedAt;
  const remaining = referralsRemaining(verified);
  const pct = (referralProgress(verified) / REFERRAL_GOAL) * 100;

  const ctaLabel = earned
    ? "View your reward"
    : verified > 0
      ? `Invite ${remaining} more`
      : "Invite friends & earn";

  if (compact) {
    return (
      <Link
        href="/dashboard/referrals"
        className={cn(
          "group flex items-center gap-2.5 rounded-lg border border-success/30 bg-success/5 px-3 py-2.5 text-sm transition-colors hover:bg-success/10",
          className,
        )}
      >
        <Gift className="h-4 w-4 shrink-0 text-success" />
        <span className="min-w-0 flex-1">
          <span className="font-medium">Get a free week of Buyer Plus</span>{" "}
          <span className="text-muted-foreground">— invite {REFERRAL_GOAL} friends for unlimited downloads.</span>
        </span>
        <ArrowRight className="h-4 w-4 shrink-0 text-success opacity-0 transition-opacity group-hover:opacity-100" />
      </Link>
    );
  }

  return (
    <div className={cn("rounded-xl border border-success/30 bg-gradient-to-br from-success/10 to-success/[0.03] p-4 sm:p-5", className)}>
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-success/15 p-2.5 text-success shrink-0">
          {earned ? <Trophy className="h-5 w-5" /> : <Gift className="h-5 w-5" />}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-display font-semibold">
            {earned ? "You earned a free week of Buyer Plus 🎉" : "Get a free week of Buyer Plus — on us"}
          </h3>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {earned
              ? "Your reward is active — enjoy unlimited downloads and Buyer Plus perks."
              : `Invite ${REFERRAL_GOAL} friends who verify their email and unlock ${REFERRAL_REWARD_DAYS} days of Buyer Plus: unlimited downloads, unlimited free claims, and 5% off every purchase.`}
          </p>

          {!earned && verified > 0 && (
            <div className="mt-3 space-y-1">
              <Progress value={pct} className="h-1.5" />
              <p className="text-xs text-muted-foreground font-mono">{verified} / {REFERRAL_GOAL} verified invites</p>
            </div>
          )}

          <Button asChild size="sm" className="mt-3 gap-1.5">
            <Link href="/dashboard/referrals">
              <Gift className="h-3.5 w-3.5" />
              {ctaLabel}
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
